import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { saveAuthReturnTo, useAuth } from '@repo/common';
import { getMyBoards } from '../../api/twoDays/board.api';
import { STATUS_CHIP, rangeLabel } from '../../components/TwoDays/ops/opsModel';
import { StatusChip } from '../../components/TwoDays/primitives';
import { PILOT_PROJECT_ID } from '../../components/TwoDays/model/mockData';
import TdButton from '../../components/TwoDays/Button';

/**
 * /2days — 내 팀 보드 목록.
 * 팀원은 합류한 팀, 스태프 계정은 모든 프로젝트의 모든 팀 (팀원이 아닌 팀은 읽기 전용).
 * 팀원이고 보드가 하나뿐이면 바로 그 보드로 간다.
 */
const TwoDaysBoards = () => {
  const navigate = useNavigate();
  const { isLoggedIn, loading } = useAuth() ?? {};
  const [boards, setBoards] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (loading || !isLoggedIn) return;
    getMyBoards()
      .then(setBoards)
      .catch(() => setFailed(true));
  }, [loading, isLoggedIn]);

  const goLogin = () => {
    saveAuthReturnTo('/2days');
    navigate('/login', { state: { returnTo: '/2days' } });
  };

  if (!loading && !isLoggedIn) {
    return (
      <Center>
        <p className='text-[18px] font-medium text-text-primary'>Log in to see your team boards</p>
        <TdButton onClick={goLogin} className='mt-2'>
          Log in with Koready
        </TdButton>
      </Center>
    );
  }
  if (boards?.length === 1 && boards[0].access === 'member') {
    return <Navigate replace to={`/2days/board/${boards[0].slug}`} />;
  }

  return (
    <div className='min-h-screen bg-bg-twodays px-4 py-10 sm:py-14'>
      <div className='mx-auto flex w-full max-w-[720px] flex-col gap-6'>
        <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
          Your team boards
        </h1>
        {failed ? (
          <p className='text-[14px] text-text-secondary'>
            Couldn’t load your boards. Refresh the page.
          </p>
        ) : boards == null ? (
          <div className='h-40 animate-pulse rounded-2xl bg-grayscale-gray00' />
        ) : boards.length === 0 ? (
          <p className='rounded-2xl border border-dashed border-green-deep03/20 bg-grayscale-gray00 px-6 py-12 text-center text-[14px] text-text-secondary'>
            You’re not on a team yet. Open the team link from the Koready team to join.
          </p>
        ) : (
          <ul className='divide-y divide-green-deep03/[0.08] rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'>
            {boards.map((b) => (
              <li key={b.slug}>
                <Link
                  to={`/2days/board/${b.slug}`}
                  className='flex items-center gap-4 px-5 py-4 hover:bg-grayscale-gray01'
                >
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='truncate text-[15px] font-medium text-text-primary'>
                      {b.teamName}
                    </span>
                    <span className='truncate text-[13px] text-text-secondary'>
                      {b.projectName} · {rangeLabel(b.startAt, b.endAt)}
                    </span>
                  </span>
                  {b.access === 'staff' && (
                    <StatusChip chip={{ label: 'Staff · read-only', tone: 'accent' }} />
                  )}
                  <StatusChip chip={STATUS_CHIP[b.status] ?? STATUS_CHIP.draft} />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {process.env.NODE_ENV !== 'production' && (
          <Link
            to={`/2days/projects/${PILOT_PROJECT_ID}/tasks`}
            className='w-fit text-[13px] text-text-secondary underline underline-offset-2'
          >
            Open the mock pilot board (dev)
          </Link>
        )}
      </div>
    </div>
  );
};

const Center = ({ children }) => (
  <div className='flex min-h-screen flex-col items-center justify-center gap-2 bg-bg-twodays p-6 text-center'>
    {children}
  </div>
);

export default TwoDaysBoards;
